import React from 'react';
import { Box } from '@mui/material';

import { ALE, statusBadgeColors } from './tokens';

/** Generic ghost badge (rounded chip) used across the table + drawer. */
export const Badge: React.FC<{
    bg: string;
    fg: string;
    children: React.ReactNode;
    sx?: object;
}> = ({ bg, fg, children, sx }) => (
    <Box
        component="span"
        sx={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 600,
            fontSize: '11.5px',
            letterSpacing: '.2px',
            padding: '3px 9px',
            borderRadius: '7px',
            lineHeight: 1.4,
            whiteSpace: 'nowrap',
            bgcolor: bg,
            color: fg,
            ...sx,
        }}
    >
        {children}
    </Box>
);

export const StatusBadge: React.FC<{ code: number; label?: string }> = ({ code, label }) => {
    const { bg, fg } = statusBadgeColors(code);
    return <Badge bg={bg} fg={fg}>{label ?? code}</Badge>;
};

export const MethodBadge: React.FC<{ method: string }> = ({ method }) => (
    <Badge bg={ALE.neutralBg} fg={ALE.neutralT}>{method}</Badge>
);

export const NeutralBadge: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <Badge bg={ALE.neutralBg} fg={ALE.neutralT}>{children}</Badge>
);

export const InfoBadge: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <Badge bg={ALE.infoBg} fg={ALE.infoT}>{children}</Badge>
);

export const WarnBadge: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <Badge bg={ALE.warnBg} fg={ALE.warnT}>{children}</Badge>
);

/** Boolean flag chip (on = info, off = muted) used in the overview grid. */
export const FlagChip: React.FC<{ on: boolean; label: string }> = ({ on, label }) => (
    <Box
        component="span"
        sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '5px',
            fontSize: '11.5px',
            fontWeight: 600,
            padding: '2px 9px',
            borderRadius: '6px',
            bgcolor: on ? ALE.infoBg : ALE.g200,
            color: on ? ALE.infoT : ALE.g500,
        }}
    >
        {on ? '✓' : '✕'} {label}
    </Box>
);
